import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { displayPhone } from '../../lib/phone';

// Service providers attached to a horse. Fetches its own data
// (GET /providers?horseId=) so the horse detail route stays untouched.
export default function ProvidersCard({ horseId }) {
  const { activeRole } = useAuth();
  const canEdit = activeRole === 'owner' || activeRole === 'manager';
  const qc = useQueryClient();
  const [picking, setPicking] = useState(false);

  const attached = useQuery({
    queryKey: ['horse-providers', horseId],
    queryFn: () => api.get(`/providers?horseId=${horseId}`),
  });

  const detach = useMutation({
    mutationFn: (providerId) => api.del(`/providers/${providerId}/horses/${horseId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['horse-providers', horseId] }),
  });

  const list = attached.data?.providers ?? [];

  return (
    <section className="card p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-medium">Service providers</h2>
        {canEdit && !picking && (
          <button onClick={() => setPicking(true)} className="text-sm text-ink-600 hover:text-ink-900">
            + Attach
          </button>
        )}
      </div>

      {attached.isLoading && <p className="text-sm text-ink-400">Loading…</p>}
      {!attached.isLoading && list.length === 0 && !picking && (
        <p className="text-sm text-ink-400">No service providers attached.</p>
      )}

      {list.length > 0 && (
        <ul className="divide-y divide-ink-100">
          {list.map((p) => (
            <li key={p.id} className="py-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-ink-900">{p.name}</span>
                  {p.relationship && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-ink-100 text-ink-600">
                      {p.relationship}
                    </span>
                  )}
                  {p.specialties?.map((s) => (
                    <span
                      key={s}
                      className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700"
                    >
                      {s}
                    </span>
                  ))}
                </div>
                {p.businessName && <div className="text-sm text-ink-500">{p.businessName}</div>}
                <div className="text-sm text-ink-500">
                  {(p.phones ?? []).map((ph, i) => (
                    <span key={i}>
                      {i > 0 && ' · '}
                      {ph.label ? `${ph.label}: ` : ''}
                      {displayPhone(ph.number)}
                    </span>
                  ))}
                </div>
                {p.email && <div className="text-sm text-ink-500 truncate">{p.email}</div>}
              </div>
              {canEdit && (
                <button
                  onClick={() => detach.mutate(p.id)}
                  disabled={detach.isPending}
                  className="text-ink-300 hover:text-red-600 shrink-0"
                  aria-label="Detach provider"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {picking && (
        <AttachPicker horseId={horseId} attachedIds={list.map((p) => p.id)} onDone={() => setPicking(false)} />
      )}
    </section>
  );
}

function AttachPicker({ horseId, attachedIds, onDone }) {
  const qc = useQueryClient();
  const all = useQuery({ queryKey: ['providers'], queryFn: () => api.get('/providers') });
  const [providerId, setProviderId] = useState('');
  const [relationship, setRelationship] = useState('');

  const attach = useMutation({
    mutationFn: () =>
      api.post(`/providers/${providerId}/horses/${horseId}`, {
        relationship: relationship.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['horse-providers', horseId] });
      onDone();
    },
  });

  const available = (all.data?.providers ?? []).filter((p) => !attachedIds.includes(p.id));

  return (
    <div className="mt-3 pt-3 border-t border-ink-100 space-y-2">
      {all.isLoading ? (
        <p className="text-sm text-ink-400">Loading providers…</p>
      ) : available.length === 0 ? (
        <p className="text-sm text-ink-500">
          No more providers to attach.{' '}
          <Link to="/providers" className="underline">
            Add one in the directory
          </Link>
          .
        </p>
      ) : (
        <>
          <select
            className="input"
            value={providerId}
            onChange={(e) => setProviderId(e.target.value)}
          >
            <option value="">Select a provider…</option>
            {available.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.businessName ? ` — ${p.businessName}` : ''}
              </option>
            ))}
          </select>
          <input
            className="input"
            placeholder="Relationship (optional, e.g. Primary vet)"
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
          />
          {attach.isError && <p className="text-sm text-red-600">Couldn’t attach. Try again.</p>}
          <div className="flex gap-2">
            <button
              onClick={() => attach.mutate()}
              disabled={!providerId || attach.isPending}
              className="btn-primary"
            >
              {attach.isPending ? 'Attaching…' : 'Attach'}
            </button>
            <button onClick={onDone} className="text-sm text-ink-500 hover:text-ink-800 px-2">
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}
